import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  MagnifyingGlassIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  FunnelIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { ChartBarIcon, ShieldCheckIcon, DocumentTextIcon, Squares2X2Icon } from '@heroicons/react/24/solid';

// Agent metadata
const AGENT_INFO = {
  'valuation_chat_agent': {
    name: 'Valuation Specialist',
    icon: ChartBarIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  'continuity_guardian_agent': {
    name: 'Continuity Specialist',
    icon: ShieldCheckIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50'
  },
  'content_creation_copilot_agent': {
    name: 'Content Co-Pilot',
    icon: DocumentTextIcon,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  'succession_dashboard_agent': {
    name: 'Succession Dashboard',
    icon: Squares2X2Icon,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50'
  }
};

/**
 * ConversationHistory - List and search past conversations
 *
 * Features:
 * - Search conversations
 * - Filter by agent
 * - Sort by date/activity
 * - Quick actions (view, delete, resume)
 */
const ConversationHistory = ({
  conversations = [],
  onSelect,
  onDelete,
  onOpenChat
}) => {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAgent, setFilterAgent] = useState('all');
  const [sortBy, setSortBy] = useState('lastActivity'); // lastActivity, startedAt

  // Filter and sort conversations
  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];

    // Filter by agent
    if (filterAgent !== 'all') {
      filtered = filtered.filter(c => c.agentType === filterAgent);
    }

    // Search (by agent name for now, could search messages later)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => {
        const agentName = AGENT_INFO[c.agentType]?.name.toLowerCase() || '';
        return agentName.includes(query) || c.id.toLowerCase().includes(query);
      });
    }

    // Sort
    filtered.sort((a, b) => {
      const aTime = new Date(a[sortBy]);
      const bTime = new Date(b[sortBy]);
      return bTime - aTime; // Newest first
    });

    return filtered;
  }, [conversations, searchQuery, filterAgent, sortBy]);

  // Get unique agent types from conversations
  const agentTypes = useMemo(() => {
    const types = new Set(conversations.map(c => c.agentType));
    return Array.from(types);
  }, [conversations]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return intl.formatMessage({ id: 'succession.agent_system.conversation_history.just_now' });
    if (diffMins < 60) return intl.formatMessage({ id: 'succession.agent_system.conversation_history.minutes_ago' }, { count: diffMins });
    if (diffHours < 24) return intl.formatMessage({ id: 'succession.agent_system.conversation_history.hours_ago' }, { count: diffHours });
    if (diffDays < 7) return intl.formatMessage({ id: 'succession.agent_system.conversation_history.days_ago' }, { count: diffDays });

    return date.toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const handleDelete = (e, conversationId) => {
    e.stopPropagation();
    if (confirm(intl.formatMessage({ id: 'succession.agent_system.conversation_history.confirm_delete' }))) {
      onDelete(conversationId);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-succession-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-succession-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={intl.formatMessage({ id: 'succession.agent_system.conversation_history.search_placeholder' })}
              className="w-full pl-10 pr-4 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent"
            />
          </div>

          {/* Filter by agent */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-succession-text-muted" />
            <select
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              className="px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent"
            >
              <option value="all">{intl.formatMessage({ id: 'succession.agent_system.conversation_history.all_agents' })}</option>
              {agentTypes.map(type => (
                <option key={type} value={type}>
                  {AGENT_INFO[type]?.name || type}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-succession-text-muted" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent"
            >
              <option value="lastActivity">{intl.formatMessage({ id: 'succession.agent_system.conversation_history.sort_last_activity' })}</option>
              <option value="startedAt">{intl.formatMessage({ id: 'succession.agent_system.conversation_history.sort_created' })}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-succession-text-secondary">
          {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''}
          {searchQuery && ` matching "${searchQuery}"`}
        </p>
      </div>

      {/* Conversation List */}
      {filteredConversations.length === 0 ? (
        <div className="bg-white rounded-lg border border-succession-border p-12 text-center">
          <ChatBubbleLeftRightIcon className="h-12 w-12 text-succession-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
            {intl.formatMessage({ id: 'succession.agent_system.conversation_history.no_conversations_found' })}
          </h3>
          <p className="text-sm text-succession-text-secondary">
            {searchQuery || filterAgent !== 'all'
              ? intl.formatMessage({ id: 'succession.agent_system.conversation_history.try_adjusting' })
              : intl.formatMessage({ id: 'succession.agent_system.conversation_history.start_new' })
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredConversations.map((conversation) => {
            const agentInfo = AGENT_INFO[conversation.agentType] || {};
            const Icon = agentInfo.icon || ChatBubbleLeftRightIcon;

            return (
              <div
                key={conversation.id}
                onClick={() => onSelect(conversation)}
                className="bg-white rounded-lg border border-succession-border p-4 hover:border-succession-primary hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  {/* Agent Icon */}
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${agentInfo.bgColor} flex items-center justify-center`}>
                    <Icon className={`h-6 w-6 ${agentInfo.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-succession-text-primary group-hover:text-succession-primary transition-colors">
                          {agentInfo.name || conversation.agentType}
                        </h3>
                        <p className="text-sm text-succession-text-muted mt-1">
                          {intl.formatMessage({ id: 'succession.agent_system.conversation_history.started' })} {formatDate(conversation.startedAt)}
                        </p>
                      </div>

                      {/* Unread badge */}
                      {conversation.unread > 0 && (
                        <span className="flex-shrink-0 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                          {conversation.unread}
                        </span>
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-succession-text-secondary">
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {intl.formatMessage({ id: 'succession.agent_system.conversation_history.last_activity' })} {formatDate(conversation.lastActivity)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenChat({ conversationId: conversation.id });
                      }}
                      className="p-2 text-succession-primary hover:bg-succession-primary hover:text-white rounded-lg transition-colors"
                      title={intl.formatMessage({ id: 'succession.agent_system.conversation_history.resume' })}
                    >
                      <ChatBubbleLeftRightIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, conversation.id)}
                      className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors"
                      title={intl.formatMessage({ id: 'succession.agent_system.conversation_history.delete' })}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                    <ChevronRightIcon className="h-5 w-5 text-succession-text-muted" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

ConversationHistory.propTypes = {
  conversations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    agentType: PropTypes.string.isRequired,
    startedAt: PropTypes.string.isRequired,
    lastActivity: PropTypes.string.isRequired,
    unread: PropTypes.number
  })),
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onOpenChat: PropTypes.func.isRequired
};

export default ConversationHistory;
